import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Tabs,
  Tab,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Button,
  Box,
  TextField,
  InputAdornment,
  Divider,
  Select,
  MenuItem,
  Pagination,
  FormControl,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import useFetchObjects from "../../../../api/useFetchObjects";
import useUpdate from "../../../../api/useUpdate";
import useDelete from "../../../../api/useDelete";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import PaymentOverviewDialog from "../../Dental/PatientTreatmentList/PaymentOverviewDialog";
import ImageManagementDialog from "../../Dental/PatientTreatmentList/ImageManagementDialog";

const getStatusColor = (status) => {
  switch (status) {
    case 1:
      return "warning.main";
    case 2:
      return "success.main";
    case 3:
      return "error.main";
    case 4:
      return "grey.500";
    default:
      return "info.main";
  }
};

const TreatmentRow = ({
  treatment,
  handleDeleteItem,
  handleEditClick,
  openPaymentDialog,
  openImageDialog,
  handleStatusChange,
  statusLabels,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{treatment.treatment_date}</TableCell>
        <TableCell>{treatment.patient_name}</TableCell>
        <TableCell>{treatment.dentist_name}</TableCell>
        <TableCell>
          <Typography variant="body2">
            {treatment.total_cost} ؋{" "}
            {treatment.discount > 0 && (
              <Typography
                component="span"
                variant="caption"
                color="textSecondary"
              >
                ({treatment.discount} ؋ {statusLabels.discount})
              </Typography>
            )}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            variant="subtitle2"
            color={getStatusColor(treatment.status)}
          >
            {statusLabels[treatment.status]}
          </Typography>
        </TableCell>
        <TableCell>
          <Button
            variant="outlined"
            size="small"
            onClick={() => openPaymentDialog(treatment)}
          >
            {statusLabels.viewPayments}
          </Button>
        </TableCell>
        <TableCell>
          <Button
            variant="outlined"
            size="small"
            onClick={() => openImageDialog(treatment)}
          >
            {statusLabels.manageImages}
          </Button>
        </TableCell>
        <TableCell>
          {/* Conditional Action Buttons Based on Status */}
          {treatment.status === 1 && (
            <>
              <Button
                variant="contained"
                size="small"
                color="success"
                onClick={() => handleStatusChange(treatment.id, 2)}
                sx={{ mr: 1, mb: { xs: 1, sm: 0 } }}
              >
                {statusLabels.markInProgress}
              </Button>
              <Button
                variant="contained"
                size="small"
                color="error"
                onClick={() => handleStatusChange(treatment.id, 3)}
                sx={{ mr: 1, mb: { xs: 1, sm: 0 } }}
              >
                {statusLabels.markCompleted}
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => handleStatusChange(treatment.id, 4)}
              >
                {statusLabels.markCancelled}
              </Button>
            </>
          )}
          {treatment.status === 2 && (
            <>
              <Button
                variant="contained"
                size="small"
                color="error"
                onClick={() => handleStatusChange(treatment.id, 3)}
                sx={{ mr: 1, mb: { xs: 1, sm: 0 } }}
              >
                {statusLabels.markCompleted}
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => handleStatusChange(treatment.id, 4)}
              >
                {statusLabels.markCancelled}
              </Button>
            </>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={9}
          sx={{ paddingBottom: open ? 2 : 0, paddingTop: open ? 2 : 0 }}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box>
              <Typography variant="h6" gutterBottom>
                {statusLabels.procedures}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{statusLabels.procedureName}</TableCell>
                    <TableCell>{statusLabels.tooth}</TableCell>
                    <TableCell>{statusLabels.cost}</TableCell>
                    <TableCell>{statusLabels.actions}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                  {treatment.treatment_details.map((detail) => (
                    <TableRow key={detail.id} hover>
                      <TableCell>{detail.procedure_name}</TableCell>
                      <TableCell>{detail.tooth_number}</TableCell>
                      <TableCell>{detail.cost} ؋</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditClick(detail)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteItem(detail.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const DentalPatientView = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const auth = useAuth();
  const token = auth?.user?.token;

  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [existingImages, setExistingImages] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [selectedTreatmentId, setSelectedTreatmentId] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalDentalTreatments, setTotalDentalTreatments] = useState(0);

  const status =
    tabValue === 0 ? 1 : tabValue === 1 ? 2 : tabValue === 2 ? 3 : 4;

  const { data, isLoading, refetch } = useFetchObjects(
    ["dental-treatments", status, searchTerm, page, rowsPerPage],
    `dental-treatments/?page=${page}&page_size=${rowsPerPage}&status=${status}&search=${searchTerm}`,
    token
  );

  const { handleUpdate } = useUpdate("dental-treatments", token);
  const { handleDelete, ConfirmDialog } = useDelete(
    "detail-treatment-details",
    token
  );

  useEffect(() => {
    refetch();
  }, [refetch, page, rowsPerPage, status, searchTerm]);

  useEffect(() => {
    if (data && data.count !== undefined) {
      setTotalDentalTreatments(data.count);
    }
  }, [data]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleEditClick = (detail) => {
    setSelectedDetail(detail);
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedDetail(null);
  };

  const openPaymentDialogHandler = (treatment) => {
    setSelectedTreatment(treatment);
    setPaymentDialogOpen(true);
  };

  const closePaymentDialogHandler = () => {
    setSelectedTreatment(null);
    setPaymentDialogOpen(false);
  };

  const handleStatusChange = async (treatmentId, newStatus) => {
    const formData = new FormData();
    formData.append("status", newStatus);

    await handleUpdate(treatmentId, formData);
    refetch();
  };

  const openImageDialogHandler = (treatment) => {
    setExistingImages(treatment.images || []);
    setSelectedTreatmentId(treatment.id);
    setImageDialogOpen(true);
  };

  const closeImageDialogHandler = () => {
    setImageDialogOpen(false);
    setExistingImages([]);
    setSelectedTreatmentId(null);
  };

  const handleImageUpdated = () => {
    refetch();
    setRemovedImageIds([]);
  };

  const handleRemoveExistingImage = (imageId) => {
    console.log(`Removing image with ID: ${imageId}`);
    setExistingImages((prevImages) =>
      prevImages.filter((image) => image.id !== imageId)
    );
    setRemovedImageIds((prevIds) => [...prevIds, imageId]);
  };

  const statusLabels = {
    1: t("dental.pending"),
    2: t("dental.inProgress"),
    3: t("dental.completed"),
    4: t("dental.cancelled"),
    discount: t("dental.discount"),
    viewPayments: t("dental.viewPayments"),
    manageImages: t("dental.manageImages"),
    markInProgress: t("dental.markInProgress"),
    markCompleted: t("dental.markCompleted"),
    markCancelled: t("dental.markCancelled"),
    procedures: t("dental.procedures"),
    procedureName: t("dental.procedureName"),
    tooth: t("dental.tooth"),
    cost: t("dental.cost"),
    actions: t("dental.actions"),
    searchPlaceholder: t("dental.searchPlaceholder"),
    loading: t("dental.loading"),
    noRecordsFound: t("dental.noRecordsFound"),
  };

  return (
    <Grid item xs={12}>
      <Paper sx={{ p: { xs: 2, md: 3 }, boxShadow: 3 }}>
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
            gap: 2,
          }}
        >
          <TextField
            size="small"
            variant="outlined"
            placeholder={statusLabels.searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", md: "400px" } }}
          />
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons="auto"
            sx={{
              "& .MuiTab-root": { textTransform: "capitalize", minWidth: 100 },
            }}
          >
            <Tab label={statusLabels[1]} />
            <Tab label={statusLabels[2]} />
            <Tab label={statusLabels[3]} />
            <Tab label={statusLabels[4]} />
          </Tabs>
        </Box>

        <Divider />

        {/* Table Section */}
        <TableContainer sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.grey[200] }}>
                <TableCell />
                <TableCell>{t("dental.date")}</TableCell>
                <TableCell>{t("dental.patient")}</TableCell>
                <TableCell>{t("dental.dentist")}</TableCell>
                <TableCell>{t("dental.totalCostWithDiscount")}</TableCell>
                <TableCell>{t("dental.status")}</TableCell>
                <TableCell>{statusLabels.viewPayments}</TableCell>
                <TableCell>{statusLabels.manageImages}</TableCell>
                <TableCell>{statusLabels.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ bgcolor: "#ccfcfc" }}>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body1">
                      {statusLabels.loading}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : data?.results?.length > 0 ? (
                data.results.map((treatment) => (
                  <TreatmentRow
                    key={treatment.id}
                    treatment={treatment}
                    handleDeleteItem={handleDelete}
                    handleEditClick={handleEditClick}
                    openPaymentDialog={openPaymentDialogHandler}
                    openImageDialog={openImageDialogHandler}
                    handleStatusChange={handleStatusChange}
                    statusLabels={statusLabels}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body1">
                      {statusLabels.noRecordsFound}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <PaymentOverviewDialog
          open={paymentDialogOpen}
          onClose={closePaymentDialogHandler}
          treatment={selectedTreatment}
        />

        <ImageManagementDialog
          open={imageDialogOpen}
          onClose={closeImageDialogHandler}
          treatmentId={selectedTreatmentId}
          existingImages={existingImages}
          removedImageIds={removedImageIds}
          onImageUpdated={handleImageUpdated}
          onRemoveExistingImage={handleRemoveExistingImage}
        />

        {/* Pagination Controls */}
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          justifyContent="space-between"
          alignItems="center"
          mt={4}
          mb={2}
          gap={2}
        >
          <Box display="flex" alignItems="center">
            <Typography variant="body2" mr={2}>
              {t("reception.rowsPerPage")}:
            </Typography>
            <FormControl variant="standard" sx={{ minWidth: 80 }}>
              <Select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                label={t("reception.rowsPerPage")}
              >
                {[5, 10, 25, 50, 100, 500].map((number) => (
                  <MenuItem key={number} value={number}>
                    {number}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Pagination
            count={Math.ceil(totalDentalTreatments / rowsPerPage)}
            page={page}
            onChange={handlePageChange}
            color="primary"
            variant="outlined"
            shape="rounded"
          />
        </Box>
      </Paper>
    </Grid>
  );
};

export default DentalPatientView;
